/**
 * Typed API client for the Polestar Tracker backend.
 */

import { type ResidualClassification } from "./cars";

export interface CarFilter {
  brand_codes?: string[];
  models?: string[];
  model_years?: number[];
  motor_codes?: string[];
  // Raw cars.drivetrain values ("RWD", "AWD", "FWD"). OR within the axis.
  // Option list comes from the inventory-counts drivetrains map.
  drivetrains?: string[];
  // Per-car body style in the CANONICAL vocabulary ("Saloon", "Sport Turismo",
  // "Cross Turismo", "SUV"). Distinct from body_classes, which is the
  // per-carline models.body_class taxonomy and carries one value for a whole
  // carline — it cannot express the Taycan's three bodies. Option list comes
  // from the inventory-counts body_types map.
  body_types?: string[];
  mileage_min?: number;
  mileage_max?: number;
  age_months_min?: number;
  age_months_max?: number;
  price_min?: number;
  price_max?: number;
  paints?: string[];
  wheels?: string[];
  interiors?: string[];
  packs_required?: string[];
  packs_excluded?: string[];
  // M9.A — capability ontology slugs (brand-agnostic; survive brand switch).
  capabilities_any_of?: string[];
  capabilities_excluded?: string[];
  // M11.B — class predicates from the canonical taxonomy
  // (docs/feature-class-filter.md). Brand-agnostic; survive brand switch.
  // AND across the two axes; OR within each.
  body_classes?: string[];
  size_classes?: string[];
  // #29 — model-family hierarchy. Operator-curated grouping above
  // model_code. Selections persist across brand switches (spec §2.4.6);
  // synthetic "_unmapped" family is filterable like any other.
  family_codes?: string[];
  // bafd8fd — Variant / Trim predicates (spec §2.4.2). Brand-specific;
  // resolved via the Sinope-LLM matcher tables. Cars without a matcher row
  // or with a NULL code are excluded.
  variant_codes?: string[];
  trim_codes?: string[];
  // M9.A — new raw axes that capability slugs expand into.
  wheel_diameters?: number[];
  // Rounded whole-kWh cars.battery_kwh values. OR within the axis. Option
  // list comes from the inventory-counts battery_kwh_values map.
  battery_kwh_values?: number[];
  // Normalised cars.interior_material labels (e.g. BMW iX "Atelier"/"Suite"/
  // "Loft"). OR within the axis. Option list comes from the inventory-counts
  // interior_materials map.
  interior_materials?: string[];
  options_required?: string[];
  previous_owners_max?: number;
  partner_ids?: string[];
  cycle_states?: string[];
  min_battery_soh?: number;
  include_disappeared?: boolean;
  /** Availability posture, third state — restricts to cars that LEFT stock
   *  inside `disappeared_within_days`. Supersedes `include_disappeared`.
   *  See `availabilityOf` / `withAvailability` below. */
  only_disappeared?: boolean;
  disappeared_within_days?: number;
}

/** The availability postures the sidebar dropdown offers, derived from the
 *  `include_disappeared` / `only_disappeared` / `disappeared_within_days`
 *  triple (spec §2.4.2). The two unavailable options differ only in the
 *  window: the recent one keeps the 30-day default, the all-time one widens
 *  it past any plausible scrape history.
 *
 *  `recent` (available + gone in the last 14 days) is the UI default: a filter
 *  that sets none of the three reads as `recent`, and `buildParams` sends the
 *  14-day window for it. The default lives here, not in the server's
 *  `CarFilter`, because saved filters and alert criteria deserialise through
 *  that schema and must keep the 30-day window they were created under. */
export type Availability =
  | "recent"
  | "available"
  | "unavailable"
  | "unavailable_all_time"
  | "all";

/** Server-side default for `disappeared_within_days`. */
export const AVAILABILITY_RECENT_DAYS = 30;
/** Window for the default `recent` posture (available + gone in the last N days). */
export const AVAILABILITY_DEFAULT_DAYS = 14;
/** "All time" window — wider than the project's whole scrape history. */
export const AVAILABILITY_ALL_TIME_DAYS = 3650;

export function availabilityOf(filter: CarFilter): Availability {
  if (filter.only_disappeared) {
    const days = filter.disappeared_within_days ?? AVAILABILITY_RECENT_DAYS;
    return days >= AVAILABILITY_ALL_TIME_DAYS ? "unavailable_all_time" : "unavailable";
  }
  if (filter.include_disappeared === false) return "available";
  const days = filter.disappeared_within_days ?? AVAILABILITY_DEFAULT_DAYS;
  return days === AVAILABILITY_DEFAULT_DAYS ? "recent" : "all";
}

export function withAvailability(filter: CarFilter, value: Availability): CarFilter {
  return {
    ...filter,
    include_disappeared: value !== "available",
    only_disappeared: value === "unavailable" || value === "unavailable_all_time",
    // The window is only operator-visible through this dropdown, so every
    // option sets it explicitly — otherwise picking "all time" and then
    // switching back would silently leave the widened window in place.
    disappeared_within_days:
      value === "unavailable_all_time"
        ? AVAILABILITY_ALL_TIME_DAYS
        : value === "recent"
          ? AVAILABILITY_DEFAULT_DAYS
          : AVAILABILITY_RECENT_DAYS,
  };
}

export interface CarSummary {
  car_id: string;
  brand_code: string;
  market_code: string;
  model_code: string | null;
  model_year: number | null;
  display_name: string | null;
  motor_code: string | null;
  motor_label: string | null;
  drivetrain: string | null;
  exterior_code: string | null;
  interior_code: string | null;
  wheels_code: string | null;
  exterior_label: string | null;
  interior_label: string | null;
  wheels_label: string | null;
  price_retail_gbp: number | null;
  /** M8 — regression's fitted ("expected") price; null when no usable fit. */
  expected_price_gbp: number | null;
  /** M8 — asking price minus expected (negative = below expected). */
  price_difference_gbp: number | null;
  /** gck303/evtracker#60 — the same classification the leaderboard and
   *  detail breakdown use (error-scaled z when the current-fit snapshot has
   *  one, else the percentage-band fallback); null when there's no usable
   *  snapshot (price_difference_gbp is also null then). */
  vs_expected_classification: ResidualClassification | null;
  /** #60 — true for a high-leverage car: the £ figure still renders, but the
   *  band above is not a meaningful value signal for it. */
  vs_expected_no_peers: boolean;
  /** Net £ the asking price has fallen over the last 7 days (positive), or
   *  null when it hasn't fallen. Dated by when we observed it. */
  price_cut_gbp: number | null;
  /** When the most recent reduction in that window was seen. */
  price_cut_at: string | null;
  mileage_distance: number | null;
  mileage_metric: string | null;
  first_registered_at: string | null;
  /** Polestar's own `structureWeek`, raw (`"202423"`). Null on every other
   *  brand — no other payload carries the field. */
  structure_week: string | null;
  /** Monday of that ISO week. An anchor, not a known day — week resolution. */
  structure_week_start: string | null;
  /** first_registered_at − structure_week_start, in days. Positive is normal;
   *  negative is real and deliberately not clamped. */
  registration_offset_days: number | null;
  /** UK age identifier for the registration date ('24', '74', …), derived from
   *  the date rather than the plate string. */
  registration_plate_identifier: string | null;
  /** VIN positions 12-17 verbatim ("000905"). A string, so leading zeros
   *  survive and non-numeric tails still render. */
  vin_serial: string | null;
  first_seen_at: string;
  last_seen_at: string;
  status: string | null;
  /** M13.B — DVLA-derived sale state: 'unknown' | 'sold' | 'exported'. */
  sale_status: string;
  sale_status_inferred_at: string | null;
  battery_soh_value: number | null;
  partner_id: string | null;
  handover_city: string | null;
  handover_name: string | null;
  hero_photo_url: string | null;
  hero_photo_sha256: string | null;
  /** Whether *this* signed-in user has starred the car. Always false when the
   *  request is anonymous — favourites are per-user server state. */
  is_favourite: boolean;
}

export interface CarListResponse {
  cars: CarSummary[];
  total: number;
}

export interface EventItem {
  id: number;
  event_type: string;
  field_name: string | null;
  old_value: unknown;
  new_value: unknown;
  observed_at: string;
}

export interface DvlaEventItem {
  id: number;
  event_type: string;
  prior_value: unknown;
  new_value: unknown;
  occurred_at: string;
}

export interface PhotoItem {
  url: string;
  position: number | null;
  is_hero: boolean;
  local_path: string | null;
  sha256: string | null;
}

export interface CarDetail {
  car_id: string;
  brand_code: string;
  market_code: string;
  vin: string | null;
  pno34: string | null;
  registration_number: string | null;
  model_code: string | null;
  model_year: number | null;
  display_name: string | null;
  edition: string | null;
  structure_week: string | null;
  motor_code: string | null;
  motor_label: string | null;
  drivetrain: string | null;
  seats: number | null;
  fuel_type: string;
  exterior_code: string | null;
  interior_code: string | null;
  wheels_code: string | null;
  pilot_pack_code: string | null;
  plus_pack_code: string | null;
  performance_pack_code: string | null;
  pro_pack_code: string | null;
  plus_pro_pack_code: string | null;
  climate_pack_code: string | null;
  exterior_label: string | null;
  interior_label: string | null;
  wheels_label: string | null;
  pilot_pack_label: string | null;
  plus_pack_label: string | null;
  performance_pack_label: string | null;
  pro_pack_label: string | null;
  plus_pro_pack_label: string | null;
  climate_pack_label: string | null;
  price_retail_gbp: number | null;
  price_vat_gbp: number | null;
  price_base_ex_vat_gbp: number | null;
  currency: string | null;
  mileage_distance: number | null;
  mileage_metric: string | null;
  first_registered_at: string | null;
  /** Manufacture derivations — see `app/util/manufacture.py`. Polestar-only. */
  structure_week_start: string | null;
  registration_offset_days: number | null;
  /** Two ages that disagree: the V5C one the market prices, and the build one
   *  the battery follows. Computed server-side against UTC now. */
  age_from_structure_week_years: number | null;
  age_from_registration_years: number | null;
  /** VIN positional decode. Plant is the raw character — no plant→city map is
   *  shipped, because that mapping is inference rather than decode. */
  vin_plant_code: string | null;
  vin_production_serial: number | null;
  cycle_state: string | null;
  status: string | null;
  /** M13.B — DVLA-derived sale state: 'unknown' | 'sold' | 'exported'. */
  sale_status: string;
  sale_status_inferred_at: string | null;
  first_seen_at: string;
  last_seen_at: string;
  last_detail_fetched_at: string | null;
  source_url: string | null;
  warranty_until: string | null;
  external_notes: string | null;
  previous_owners: number | null;
  battery_soh_value: number | null;
  battery_soh_label: string | null;
  battery_kwh: number | null;
  nominal_range_miles: number | null;
  top_speed_label: string | null;
  power_label: string | null;
  acceleration_label: string | null;
  trunk_label: string | null;
  consumption_avg_value: string | null;
  consumption_avg_unit: string | null;
  partner_id: string | null;
  handover_city: string | null;
  handover_name: string | null;
  /** Whether this signed-in user has starred the car; false when anonymous. */
  is_favourite: boolean;
  /** M9.A — canonical capability labels per axis ("interior", "exterior",
   *  "wheels", "packs"), derived from capability_mappings for the car's
   *  raw codes. Empty when the operator hasn't mapped them yet. */
  capability_labels: Record<string, string[]>;
  events: EventItem[];
  /** M13.B — DVLA-driven state transitions (tax_renewed, marked_for_export). */
  dvla_events: DvlaEventItem[];
  photos: PhotoItem[];
}

export interface UserInfo {
  user_id: string;
  email: string;
}

export interface SavedFilter {
  id: string;
  user_id: string;
  name: string;
  filter_json: Record<string, unknown>;
  alerts_enabled: boolean;
  alert_cadence: string;
  created_at: string;
  updated_at: string;
}

export interface SavedFilterCreate {
  name: string;
  filter_json: Record<string, unknown>;
  alerts_enabled?: boolean;
  alert_cadence?: string;
}

export interface SavedFilterUpdate {
  name?: string;
  filter_json?: Record<string, unknown>;
  alerts_enabled?: boolean;
  alert_cadence?: string;
}

// Analytical views (spec §2.5)

export interface DepreciationPoint {
  age_months: number;
  price_gbp: number;
  sample_size: number;
}

export interface DepreciationSeries {
  model_code: string;
  motor_code: string | null;
  motor_label: string | null;
  points: DepreciationPoint[];
  baseline_price_gbp: number | null;
  baseline_age_months: number | null;
}

export interface DepreciationResponse {
  series: DepreciationSeries[];
  context: string;
  coverage_disclosure: string | null;
}

export interface ScatterPoint {
  car_id: string;
  model_code: string;
  motor_label: string | null;
  display_name: string | null;
  mileage: number;
  price_gbp: number;
}

export interface ScatterRegression {
  model_code: string;
  slope: number;
  intercept: number;
  sample_size: number;
}

export interface ScatterResponse {
  points: ScatterPoint[];
  regressions: ScatterRegression[];
  context: string;
  coverage_disclosure: string | null;
}

/** One car on the price-vs-age scatter (mirror of {@link ScatterPoint}). */
export interface PriceVsAgePoint {
  car_id: string;
  model_code: string;
  motor_label: string | null;
  display_name: string | null;
  age_months: number;
  price_gbp: number;
}

/** One sampled point on a fitted trend curve. */
export interface PriceVsAgeCurvePoint {
  age_months: number;
  price_gbp: number;
}

/**
 * Per-model log-age fit: `price = intercept + log_slope × ln(age)`.
 *
 * `curve` is the fit already evaluated server-side across the model's *observed*
 * age range, so the chart draws a polyline without knowing the functional form —
 * and cannot extrapolate past the data.
 */
export interface PriceVsAgeRegression {
  model_code: string;
  intercept: number;
  log_slope: number;
  sample_size: number;
  curve: PriceVsAgeCurvePoint[];
}

export interface PriceVsAgeResponse {
  points: PriceVsAgePoint[];
  regressions: PriceVsAgeRegression[];
  context: string;
  coverage_disclosure: string | null;
}

export interface TimeOnLotSummary {
  model_code: string;
  sample_size: number;
  min_days: number;
  q1_days: number;
  median_days: number;
  q3_days: number;
  max_days: number;
  mean_days: number;
}

export interface TimeOnLotResponse {
  summaries: TimeOnLotSummary[];
  context: string;
  coverage_disclosure: string | null;
}

// Stage 3 of price-vs-range — see /api/analytics/range.
export type RangeType =
  | "real"
  | "city_cold"
  | "city_mild"
  | "highway_cold"
  | "highway_mild"
  | "combined_cold"
  | "combined_mild";

export interface RangePoint {
  car_id: string;
  model_code: string;
  display_name: string | null;
  price_retail_gbp: number;
  range_miles: number;
  matched_variant_code: string;
  model_year: number | null;
  first_seen_at: string;
}

export interface RangeResponse {
  range_type: string;
  range_label: string;
  points: RangePoint[];
  unmatched_count: number;
  null_for_this_range_count: number;
  total_filtered_count: number;
  context: string;
  coverage_disclosure: string | null;
}

// §2.5.9 — price-vs-efficiency scatter (sibling of §2.5.8).
export type EfficiencyVsPriceType = "real" | "residual";

export interface EfficiencyPoint {
  car_id: string;
  brand_code: string;
  model_code: string;
  display_name: string | null;
  price_retail_gbp: number;
  efficiency_value_wh_per_km: number;
  matched_variant_code: string;
  model_year: number | null;
  first_seen_at: string;
}

export interface EfficiencyVsPriceResponse {
  efficiency_type: EfficiencyVsPriceType;
  efficiency_label: string;
  points: EfficiencyPoint[];
  unmatched_count: number;
  null_for_this_efficiency_count: number;
  total_filtered_count: number;
  context: string;
  coverage_disclosure: string | null;
}

// §2.5.10 — per-variant cross-brand scatter.
export interface EfficiencyByVariantPoint {
  brand_code: string;
  market_code: string;
  model_code: string;
  variant_code: string;
  display_name: string | null;
  efficiency_value_wh_per_km: number;
  median_price_gbp: number;
  inventory_count: number;
}

export interface EfficiencyByVariantResponse {
  efficiency_type: EfficiencyVsPriceType;
  efficiency_label: string;
  points: EfficiencyByVariantPoint[];
  total_filtered_count: number;
  unmatched_variants_count: number;
  null_for_this_efficiency_variants_count: number;
  coverage_disclosure: string | null;
  context: string | null;
}

// Per-variant tech-score-residual vs price-spread scatter.
export interface PriceSpreadPoint {
  brand_code: string;
  market_code: string;
  model_code: string;
  variant_code: string;
  display_name: string | null;
  model_display_name: string | null;
  efficiency_residual_wh_per_km: number;
  price_low_gbp: number;
  price_high_gbp: number;
  inventory_count: number;
}

export interface PriceSpreadResponse {
  points: PriceSpreadPoint[];
  total_filtered_count: number;
  variants_without_residual_count: number;
  context: string | null;
  coverage_disclosure: string | null;
}

// Per-variant 1-stop-range vs price-spread scatter (§2.5.11).
export interface RangeSpreadPoint {
  brand_code: string;
  market_code: string;
  model_code: string;
  variant_code: string;
  display_name: string | null;
  model_display_name: string | null;
  long_distance_1_stop_miles: number;
  price_low_gbp: number;
  price_high_gbp: number;
  inventory_count: number;
}

export interface RangeSpreadResponse {
  points: RangeSpreadPoint[];
  total_filtered_count: number;
  variants_without_range_count: number;
  context: string | null;
  coverage_disclosure: string | null;
}

// M14.C — variant-aggregated scatter (§4.5).
export interface RangeByVariantPoint {
  brand_code: string;
  market_code: string;
  model_code: string;
  variant_code: string;
  display_name: string | null;
  range_miles: number;
  median_price_gbp: number;
  inventory_count: number;
}

export interface RangeByVariantResponse {
  range_type: string;
  range_label: string;
  points: RangeByVariantPoint[];
  total_filtered_count: number;
  unmatched_variants_count: number;
  null_for_this_range_variants_count: number;
  coverage_disclosure: string | null;
  context: string | null;
}

export function buildParams(filter: CarFilter): string {
  const p = new URLSearchParams();
  filter.brand_codes?.forEach((b) => p.append("brand_codes", b));
  filter.models?.forEach((m) => p.append("models", m));
  filter.model_years?.forEach((y) => p.append("model_years", String(y)));
  filter.motor_codes?.forEach((c) => p.append("motor_codes", c));
  filter.drivetrains?.forEach((d) => p.append("drivetrains", d));
  filter.body_types?.forEach((b) => p.append("body_types", b));
  if (filter.mileage_min !== undefined) p.set("mileage_min", String(filter.mileage_min));
  if (filter.mileage_max !== undefined) p.set("mileage_max", String(filter.mileage_max));
  if (filter.age_months_min !== undefined)
    p.set("age_months_min", String(filter.age_months_min));
  if (filter.age_months_max !== undefined)
    p.set("age_months_max", String(filter.age_months_max));
  if (filter.price_min !== undefined) p.set("price_min", String(filter.price_min));
  if (filter.price_max !== undefined) p.set("price_max", String(filter.price_max));
  filter.paints?.forEach((c) => p.append("paints", c));
  filter.wheels?.forEach((c) => p.append("wheels", c));
  filter.interiors?.forEach((c) => p.append("interiors", c));
  filter.packs_required?.forEach((c) => p.append("packs_required", c));
  filter.packs_excluded?.forEach((c) => p.append("packs_excluded", c));
  filter.capabilities_any_of?.forEach((s) => p.append("capabilities_any_of", s));
  filter.capabilities_excluded?.forEach((s) => p.append("capabilities_excluded", s));
  filter.body_classes?.forEach((c) => p.append("body_classes", c));
  filter.size_classes?.forEach((c) => p.append("size_classes", c));
  filter.family_codes?.forEach((c) => p.append("family_codes", c));
  filter.variant_codes?.forEach((c) => p.append("variant_codes", c));
  filter.trim_codes?.forEach((c) => p.append("trim_codes", c));
  filter.wheel_diameters?.forEach((d) => p.append("wheel_diameters", String(d)));
  filter.battery_kwh_values?.forEach((d) => p.append("battery_kwh_values", String(d)));
  filter.interior_materials?.forEach((m) => p.append("interior_materials", m));
  filter.options_required?.forEach((c) => p.append("options_required", c));
  if (filter.previous_owners_max !== undefined)
    p.set("previous_owners_max", String(filter.previous_owners_max));
  filter.partner_ids?.forEach((id) => p.append("partner_ids", id));
  filter.cycle_states?.forEach((s) => p.append("cycle_states", s));
  if (filter.min_battery_soh !== undefined)
    p.set("min_battery_soh", String(filter.min_battery_soh));
  if (filter.include_disappeared !== undefined)
    p.set("include_disappeared", String(filter.include_disappeared));
  if (filter.only_disappeared !== undefined)
    p.set("only_disappeared", String(filter.only_disappeared));
  // An unset window means the UI's default `recent` posture (see
  // `availabilityOf`), so send its 14 days rather than let the server apply
  // its own 30-day default. Irrelevant (and harmless) when available-only.
  if (filter.disappeared_within_days !== undefined)
    p.set("disappeared_within_days", String(filter.disappeared_within_days));
  else if (!filter.only_disappeared)
    p.set("disappeared_within_days", String(AVAILABILITY_DEFAULT_DAYS));
  return p.toString();
}

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, options);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { message?: string; detail?: string };
    throw new Error(body.detail ?? body.message ?? `HTTP ${res.status}`);
  }
  if (res.status === 204) return undefined as unknown as T;
  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Brands (M9 — multi-brand UI surfacing)
// ---------------------------------------------------------------------------

export interface BrandEntry {
  brand_code: string;
  display_name: string;
  market_code: string;
  model_count: number;
  live_inventory_count: number;
  latest_scrape_at: string | null;
}

export interface BrandsResponse {
  brands: BrandEntry[];
}

export async function getBrands(): Promise<BrandsResponse> {
  return apiFetch<BrandsResponse>("/api/brands");
}

export interface LookupOption {
  code: string;
  label: string;
}

/**
 * One variant row for the filter sidebar.
 *
 * `model_code` enables grouping by Model in the tree.
 * `code` is the bare variant code from `model_specs`.
 * `composite` (`"{model_code}:{code}"`) is the stable identifier used
 * everywhere a variant_codes filter entry is read or written — URL
 * query strings, checkbox state, server filter payload. Bare codes
 * are not globally unique within a brand (e.g. Tesla `lr_awd` exists
 * under both `M3` and `MY`), so using `composite` as the React key
 * and the toggle value is what prevents cross-model collisions.
 * (Spec §2.4.2.)
 */
export interface VariantOption {
  model_code: string;
  code: string;
  label: string;
  composite: string;
}

/** One trim row for the filter sidebar. Same shape as {@link VariantOption}. */
export interface TrimOption {
  model_code: string;
  code: string;
  label: string;
  composite: string;
}

export interface BrandOptionsResponse {
  models: LookupOption[];
  packs: LookupOption[];
  interiors: LookupOption[];
  wheels: LookupOption[];
  /** bafd8fd — operator-curated variants from model_specs. Empty when not seeded. */
  variants: VariantOption[];
  /** bafd8fd — operator-curated trims from model_trims. Empty when not seeded. */
  trims: TrimOption[];
}

export async function getBrandOptions(
  brandCode: string,
  marketCode = "gb",
): Promise<BrandOptionsResponse> {
  return apiFetch<BrandOptionsResponse>(
    `/api/brands/${encodeURIComponent(brandCode)}/options?market_code=${encodeURIComponent(marketCode)}`,
  );
}

// ---------------------------------------------------------------------------
// Classes (M11.B — class filter taxonomy)
// ---------------------------------------------------------------------------

export interface ClassOption {
  code: string;
  label: string;
  count: number;
}

export interface ClassesResponse {
  body_classes: ClassOption[];
  size_classes: ClassOption[];
}

export async function getClasses(
  brandCode: string,
  marketCode = "gb",
): Promise<ClassesResponse> {
  return apiFetch<ClassesResponse>(
    `/api/classes?brand_code=${encodeURIComponent(brandCode)}&market_code=${encodeURIComponent(marketCode)}`,
  );
}

// ---------------------------------------------------------------------------
// Brand model families (#29 — collapsible carline grouping for the brand page)
// ---------------------------------------------------------------------------

export interface FamilyCarline {
  model_code: string;
  display_name: string;
  /** #31 — per-family-internal body label (suv, sportback, saloon,
   *  avant, coupe, gt, crossover, hatch). Null when the operator
   *  hasn't classified the carline. */
  body_style?: string | null;
  /** Live-car count for this carline in the active brand inventory.
   *  Surfaced as a bracketed suffix in the FilterSidebar. Optional in
   *  the type because test fixtures predate the field; absent =
   *  render `(0)`. */
  count?: number;
}

export interface FamilyEntry {
  /** Curated stable identifier; or "_unmapped" for the synthetic
   *  bucket of carlines without a YAML home. */
  family_code: string;
  display_name: string;
  sort_order: number;
  carlines: FamilyCarline[];
  /** Live-car aggregates over the family's union of carlines. */
  count: number;
  min_price_pence: number | null;
  median_price_pence: number | null;
  max_price_pence: number | null;
  age_months_min: number | null;
  age_months_max: number | null;
}

export interface BrandFamiliesResponse {
  families: FamilyEntry[];
}

export async function getFamilies(
  brandCode: string,
  options: {
    marketCode?: string;
    includeDisappeared?: boolean;
    /** Faceted counts — when provided, the full current filter is sent
     *  so per-carline/family counts exclude-self and reflect the active
     *  selection (rather than the brand-wide unfiltered baseline). */
    filter?: CarFilter;
  } = {},
): Promise<BrandFamiliesResponse> {
  const { marketCode = "gb", includeDisappeared = true, filter } = options;
  const params = new URLSearchParams(filter ? buildParams(filter) : undefined);
  params.set("market_code", marketCode);
  // buildParams already emits include_disappeared when the filter sets it
  // explicitly — only fall back to the option default when it doesn't.
  if (filter?.include_disappeared === undefined) {
    params.set("include_disappeared", String(includeDisappeared));
  }
  return apiFetch<BrandFamiliesResponse>(
    `/api/brands/${encodeURIComponent(brandCode)}/families?${params.toString()}`,
  );
}

/** Per-option car counts for the FilterSidebar bracketed suffixes.
 *
 *  Variant and Trim keys are the composite ``"{model_code}:{code}"``
 *  string (spec §2.4.2) — the same identifier the filter uses.
 *  Counts honour the same live-vs-disappeared posture as the inventory
 *  list (caller passes `includeDisappeared` to match the filter), so
 *  the sidebar numbers stay aligned with what the list renders.
 */
export interface InventoryCountsResponse {
  total: number;
  models: Record<string, number>;
  variants: Record<string, number>;
  trims: Record<string, number>;
  body_classes: Record<string, number>;
  size_classes: Record<string, number>;
  /** Integer `cars.model_year` value (string int key, e.g. "2024") →
   *  live-car count. Keys double as the sidebar's Model year option list
   *  (no seeded lookup table). */
  model_years: Record<string, number>;
  /** Raw `cars.drivetrain` value → live-car count. Keys double as the
   *  sidebar's Drivetrain option list (no seeded lookup table). */
  drivetrains: Record<string, number>;
  body_types: Record<string, number>;
  /** Rounded whole-kWh `cars.battery_kwh` value (string int key) → live-car
   *  count. Keys double as the sidebar's Battery option list (no seeded
   *  lookup table). */
  battery_kwh_values: Record<string, number>;
  /** Normalised `cars.interior_material` label → live-car count. Keys double
   *  as the sidebar's Interior option list (no seeded lookup table). */
  interior_materials: Record<string, number>;
}

export async function getInventoryCounts(
  brandCode: string,
  options: {
    marketCode?: string;
    includeDisappeared?: boolean;
    /** Faceted counts — when provided, the full current filter is sent so
     *  the server can return exclude-self counts (each axis's own
     *  selection doesn't zero itself out) across the brand-wide universe
     *  of values, including zero-count entries the UI greys out rather
     *  than hides. */
    filter?: CarFilter;
  } = {},
): Promise<InventoryCountsResponse> {
  const { marketCode = "gb", includeDisappeared = true, filter } = options;
  const params = new URLSearchParams(filter ? buildParams(filter) : undefined);
  params.set("market_code", marketCode);
  // buildParams already emits include_disappeared when the filter sets it
  // explicitly — only fall back to the option default when it doesn't.
  if (filter?.include_disappeared === undefined) {
    params.set("include_disappeared", String(includeDisappeared));
  }
  return apiFetch<InventoryCountsResponse>(
    `/api/brands/${encodeURIComponent(brandCode)}/inventory-counts?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// Cars
// ---------------------------------------------------------------------------

/** Server-side sort keys for /api/cars (spec §2.4.3, amended 2026-08-08).
 *  Sort is a view parameter, not a filter predicate — deliberately not part
 *  of CarFilter or the filterUrl round-trip, so saved filters never carry it. */
export type CarSortKey =
  | "price"
  | "model"
  | "year"
  | "motor"
  | "mileage"
  | "vs_expected"
  | "structure"
  | "reg"
  | "vin_serial"
  | "location"
  | "drivetrain"
  | "battery_soh"
  | "first_seen"
  | "last_seen";

export type SortDir = "asc" | "desc";

export async function getCars(
  filter: CarFilter = {},
  page?: {
    limit?: number;
    offset?: number;
    sort_by?: CarSortKey;
    sort_dir?: SortDir;
    /** Narrow to the signed-in user's starred cars. Deliberately a request
     *  option rather than a CarFilter axis — see §2.4.8. 401 when anonymous. */
    favourites_only?: boolean;
  },
): Promise<CarListResponse> {
  const p = new URLSearchParams(buildParams(filter));
  if (page?.limit !== undefined) p.set("limit", String(page.limit));
  if (page?.offset !== undefined) p.set("offset", String(page.offset));
  if (page?.sort_by !== undefined) p.set("sort_by", page.sort_by);
  if (page?.sort_dir !== undefined) p.set("sort_dir", page.sort_dir);
  if (page?.favourites_only) p.set("favourites_only", "true");
  const qs = p.toString();
  return apiFetch<CarListResponse>(`/api/cars${qs ? `?${qs}` : ""}`);
}

export async function getCar(carId: string): Promise<CarDetail> {
  return apiFetch<CarDetail>(`/api/cars/${carId}`);
}

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

export async function requestMagicLink(email: string): Promise<{ message: string }> {
  return apiFetch<{ message: string }>("/api/auth/request-link", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
}

export async function verifyToken(token: string): Promise<UserInfo> {
  // POST (not GET) so email-link prefetchers (Outlook/Defender Safe Links,
  // Gmail/Apple Mail link previews) cannot burn the single-use token before
  // the operator clicks. Paired with a useRef sentinel in Verify.tsx for
  // React StrictMode double-fire.
  return apiFetch<UserInfo>("/api/auth/verify-confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
}

export async function getMe(): Promise<UserInfo> {
  return apiFetch<UserInfo>("/api/auth/me");
}

export async function logout(): Promise<void> {
  await apiFetch<void>("/api/auth/logout", { method: "POST" });
}

export async function logoutAll(): Promise<void> {
  await apiFetch<void>("/api/auth/logout-all", { method: "POST" });
}

// ---------------------------------------------------------------------------
// Saved filters
// ---------------------------------------------------------------------------

export async function getFilters(): Promise<SavedFilter[]> {
  return apiFetch<SavedFilter[]>("/api/filters");
}

export async function createFilter(body: SavedFilterCreate): Promise<SavedFilter> {
  return apiFetch<SavedFilter>("/api/filters", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function updateFilter(id: string, body: SavedFilterUpdate): Promise<SavedFilter> {
  return apiFetch<SavedFilter>(`/api/filters/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function deleteFilter(id: string): Promise<void> {
  await apiFetch<void>(`/api/filters/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Favourites (§2.4.8) — server-side, per-user. Both writes are idempotent, so
// an optimistic UI can retry or double-fire without a conflict.
// ---------------------------------------------------------------------------

export interface FavouritesResponse {
  car_ids: string[];
}

export async function getFavourites(): Promise<FavouritesResponse> {
  return apiFetch<FavouritesResponse>("/api/favourites");
}

export async function addFavourite(carId: string): Promise<void> {
  await apiFetch<void>(`/api/favourites/${carId}`, { method: "PUT" });
}

export async function removeFavourite(carId: string): Promise<void> {
  await apiFetch<void>(`/api/favourites/${carId}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export async function getDepreciation(filter: CarFilter = {}): Promise<DepreciationResponse> {
  const qs = buildParams(filter);
  return apiFetch<DepreciationResponse>(`/api/analytics/depreciation${qs ? `?${qs}` : ""}`);
}

export async function getScatter(filter: CarFilter = {}): Promise<ScatterResponse> {
  const qs = buildParams(filter);
  return apiFetch<ScatterResponse>(`/api/analytics/scatter${qs ? `?${qs}` : ""}`);
}

export async function getPriceVsAge(
  filter: CarFilter = {},
): Promise<PriceVsAgeResponse> {
  const qs = buildParams(filter);
  return apiFetch<PriceVsAgeResponse>(
    `/api/analytics/price-vs-age${qs ? `?${qs}` : ""}`,
  );
}

export async function getTimeOnLot(filter: CarFilter = {}): Promise<TimeOnLotResponse> {
  const qs = buildParams(filter);
  return apiFetch<TimeOnLotResponse>(`/api/analytics/time-on-lot${qs ? `?${qs}` : ""}`);
}

export async function getRangeVsPrice(
  filter: CarFilter = {},
  rangeType: RangeType = "real",
): Promise<RangeResponse> {
  const params = new URLSearchParams(buildParams(filter));
  params.set("range_type", rangeType);
  return apiFetch<RangeResponse>(`/api/analytics/range?${params.toString()}`);
}

export async function getRangeVsPriceByVariant(
  filter: CarFilter = {},
  rangeType: RangeType = "real",
): Promise<RangeByVariantResponse> {
  const params = new URLSearchParams(buildParams(filter));
  params.set("range_type", rangeType);
  return apiFetch<RangeByVariantResponse>(
    `/api/analytics/range-by-variant?${params.toString()}`,
  );
}

export async function getEfficiencyVsPrice(
  filter: CarFilter = {},
  efficiencyType: EfficiencyVsPriceType = "real",
): Promise<EfficiencyVsPriceResponse> {
  const params = new URLSearchParams(buildParams(filter));
  params.set("efficiency_type", efficiencyType);
  return apiFetch<EfficiencyVsPriceResponse>(
    `/api/analytics/efficiency-vs-price?${params.toString()}`,
  );
}

export async function getEfficiencyVsPriceByVariant(
  filter: CarFilter = {},
  efficiencyType: EfficiencyVsPriceType = "real",
): Promise<EfficiencyByVariantResponse> {
  const params = new URLSearchParams(buildParams(filter));
  params.set("efficiency_type", efficiencyType);
  return apiFetch<EfficiencyByVariantResponse>(
    `/api/analytics/efficiency-vs-price-by-variant?${params.toString()}`,
  );
}

export async function getPriceSpread(
  filter: CarFilter = {},
): Promise<PriceSpreadResponse> {
  const qs = buildParams(filter);
  return apiFetch<PriceSpreadResponse>(
    `/api/analytics/price-spread${qs ? `?${qs}` : ""}`,
  );
}

export async function getRangeSpread(
  filter: CarFilter = {},
): Promise<RangeSpreadResponse> {
  const qs = buildParams(filter);
  return apiFetch<RangeSpreadResponse>(
    `/api/analytics/range-spread${qs ? `?${qs}` : ""}`,
  );
}

// Markdown curve — within-car price-change since listing (feature-markdown-curve.md).
export interface MarkdownCurvePoint {
  dom_bin_start: number;
  dom_bin_mid: number;
  mean_delta_gbp: number;
  median_delta_gbp: number;
  n_cars: number;
}

export interface MarkdownCurveSeries {
  brand_code: string;
  model_code: string;
  display_name: string;
  cars_clean: number;
  pct_discounted: number;
  mean_delta_day30_gbp: number | null;
  points: MarkdownCurvePoint[];
}

export interface MarkdownCurveResponse {
  series: MarkdownCurveSeries[];
  total_models: number;
  context: string | null;
  coverage_disclosure: string | null;
}

export interface MarkdownCurveOptions {
  brand_codes?: string[];
  models?: string[];
  min_cars?: number;
  max_dom_days?: number;
  bin_days?: number;
}

export async function getMarkdownCurve(
  opts: MarkdownCurveOptions = {},
): Promise<MarkdownCurveResponse> {
  const p = new URLSearchParams();
  opts.brand_codes?.forEach((b) => p.append("brand_codes", b));
  opts.models?.forEach((m) => p.append("models", m));
  if (opts.min_cars !== undefined) p.set("min_cars", String(opts.min_cars));
  if (opts.max_dom_days !== undefined) p.set("max_dom_days", String(opts.max_dom_days));
  if (opts.bin_days !== undefined) p.set("bin_days", String(opts.bin_days));
  const qs = p.toString();
  return apiFetch<MarkdownCurveResponse>(
    `/api/analytics/markdown-curve${qs ? `?${qs}` : ""}`,
  );
}

// Price changes over time — median asking per (model, reg-year) by calendar
// date, read from daily_rollup_model.
export interface PriceChangePoint {
  observed_date: string;
  median_gbp: number;
  min_gbp: number | null;
  max_gbp: number | null;
  car_count: number;
}

export interface PriceChangeSeries {
  brand_code: string;
  model_code: string;
  model_year: number;
  display_name: string;
  first_date: string;
  last_date: string;
  first_median_gbp: number;
  last_median_gbp: number;
  change_gbp: number;
  change_pct: number;
  avg_count: number;
  points: PriceChangePoint[];
}

export interface PriceChangeResponse {
  series: PriceChangeSeries[];
  total_series: number;
  window_days: number;
  context: string | null;
  coverage_disclosure: string | null;
}

export interface PriceChangeOptions {
  brand_codes?: string[];
  models?: string[];
  window_days?: number;
  min_count?: number;
  min_points?: number;
}

export async function getPriceChanges(
  opts: PriceChangeOptions = {},
): Promise<PriceChangeResponse> {
  const p = new URLSearchParams();
  opts.brand_codes?.forEach((b) => p.append("brand_codes", b));
  opts.models?.forEach((m) => p.append("models", m));
  if (opts.window_days !== undefined) p.set("window_days", String(opts.window_days));
  if (opts.min_count !== undefined) p.set("min_count", String(opts.min_count));
  if (opts.min_points !== undefined) p.set("min_points", String(opts.min_points));
  const qs = p.toString();
  return apiFetch<PriceChangeResponse>(
    `/api/analytics/price-changes${qs ? `?${qs}` : ""}`,
  );
}

// Floor price over time (§2.5.13) — the cheapest in-stock car per model by
// calendar date, read from daily_rollup_model (MIN across reg-years/markets).
export interface FloorPricePoint {
  observed_date: string;
  floor_gbp: number;
  car_count: number;
  // The car that set the floor that day, for the chart tooltip's photo + link.
  // photo_url/photo_sha256 mirror CarSummary's hero_photo_url/hero_photo_sha256
  // (sha256 present → mirrored locally via /api/photos/{sha256}); both null
  // when that car has no hero photo.
  floor_car_id: string | null;
  floor_car_photo_url: string | null;
  floor_car_photo_sha256: string | null;
}

export interface FloorPriceSeries {
  brand_code: string;
  model_code: string;
  display_name: string;
  first_date: string;
  last_date: string;
  first_floor_gbp: number;
  last_floor_gbp: number;
  change_gbp: number;
  change_pct: number;
  points: FloorPricePoint[];
}

export interface FloorPriceResponse {
  series: FloorPriceSeries[];
  total_series: number;
  window_days: number;
  context: string | null;
  coverage_disclosure: string | null;
}

export interface FloorPriceOptions {
  window_days?: number;
  min_points?: number;
}

// Sends the full CarFilter (via buildParams) so the model-selection axes —
// brand_codes, models, family_codes, body/size classes, model_years — reach the
// endpoint. Car-level predicates are accepted but ignored server-side (§2.5.13).
export async function getFloorPrice(
  filter: CarFilter = {},
  opts: FloorPriceOptions = {},
): Promise<FloorPriceResponse> {
  const p = new URLSearchParams(buildParams(filter));
  if (opts.window_days !== undefined) p.set("window_days", String(opts.window_days));
  if (opts.min_points !== undefined) p.set("min_points", String(opts.min_points));
  const qs = p.toString();
  return apiFetch<FloorPriceResponse>(
    `/api/analytics/floor-price${qs ? `?${qs}` : ""}`,
  );
}

// ---------------------------------------------------------------------------
// Stock turn (§2.5.12)
// ---------------------------------------------------------------------------

export interface StockTurnRow {
  level: "brand" | "model";
  brand_code: string;
  model_code: string | null;
  label: string;
  sample_size: number;
  reliable: boolean;
  median_days_to_sell: number | null;
  median_is_lower_bound: boolean;
  lower_bound_days: number | null;
  sell_through_30: number | null;
  sell_through_60: number | null;
  sell_through_90: number | null;
  turnover_ratio: number | null;
}

export interface StockTurnResponse {
  rows: StockTurnRow[];
  turnover_window_days: number;
  min_sample_size: number;
  context: string;
  caveats: string[];
}

export interface SpecMixRow {
  model_year: number | null;
  build_quarter: string;
  sample_size: number;
  counts: Record<string, number>;
  shares: Record<string, number>;
}

export interface SpecMixResponse {
  labels: string[];
  rows: SpecMixRow[];
  total_filtered_count: number;
  context: string;
  coverage_disclosure: string | null;
}

/** Exterior colour share by model year + build quarter. Build week is the time
 *  axis — Polestar-only, so other brands get a coverage disclosure, not a blank
 *  grid. */
export async function getColourMix(
  filter: CarFilter = {},
): Promise<SpecMixResponse> {
  const params = new URLSearchParams(buildParams(filter));
  return apiFetch<SpecMixResponse>(
    `/api/analytics/colour-mix?${params.toString()}`,
  );
}

/** Interior trim share, same grid and same build-week caveats as the colour
 *  mix. Trim codes are unfolded — the material/colour split is the signal. */
export async function getInteriorMix(
  filter: CarFilter = {},
): Promise<SpecMixResponse> {
  const params = new URLSearchParams(buildParams(filter));
  return apiFetch<SpecMixResponse>(
    `/api/analytics/interior-mix?${params.toString()}`,
  );
}

export async function getStockTurn(
  filter: CarFilter = {},
  turnoverWindowDays = 90,
): Promise<StockTurnResponse> {
  const params = new URLSearchParams(buildParams(filter));
  params.set("turnover_window_days", String(turnoverWindowDays));
  return apiFetch<StockTurnResponse>(
    `/api/analytics/stock-turn?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------------
// Capability ontology (M9.A)
// ---------------------------------------------------------------------------

export interface OntologyNode {
  /** Slug, not surrogate id. Used as the stable identifier on the wire.
   *  Synthetic unmapped pseudo-nodes use `__unmapped__{lookup}__{code}`. */
  node_id: string;
  /** Slug of the parent node, or null for top-level sections. */
  parent_node_id: string | null;
  /** Human-readable label (e.g. "Premium leather", "Heated seats",
   *  "Charcoal (RP0007)" for unmapped pseudo-nodes). */
  label: string;
  sort_order: number;
  /** Brands with at least one mapping reaching this node (descendants unioned). */
  active_brand_codes: string[];
  /** True for synthetic pseudo-nodes generated from raw codes that have
   *  no `capability_mappings` row for the requested brand. Render with
   *  monospace + faded colour per docs/feature-capability-ontology.md §4.6.
   *  Optional on the wire: only the `?brand_code=X` form returns it. */
  is_unmapped?: boolean;
}

export interface OntologyResponse {
  nodes: OntologyNode[];
}

export async function getOntology(brandCode?: string): Promise<OntologyResponse> {
  const qs = brandCode ? `?brand_code=${encodeURIComponent(brandCode)}` : "";
  return apiFetch<OntologyResponse>(`/api/ontology${qs}`);
}
